/**
 * Mandate Migration Panel
 *
 * Admin component for migrating existing GoCardless mandates.
 * Supports 3 levels:
 * 1. API Sync - Automatic sync from GoCardless
 * 2. CSV Import - Bulk import with client mapping
 * 3. Manual Association - One-by-one association
 */

import { useState, useRef } from 'react';
import {
  RefreshCw,
  Upload,
  Link2,
  Loader2,
  Check,
  AlertCircle,
  Users,
  Building2,
  ChevronDown,
  ChevronUp,
  Search,
  FileText,
  Download,
} from 'lucide-react';
import {
  useSyncMandates,
  useImportMandates,
  useAssociateMandate,
  useUnassociatedMandates,
  useLookupMandate,
} from '../hooks/useMandateMigration';
import { useGoCardlessConfig } from '../hooks/usePaymentMandates';
import type { CSVImportRow, UnassociatedMandate } from '../types/mandate.types';
import { logger } from '@/utils/logger';

type Tab = 'sync' | 'import' | 'manual';

export function MandateMigrationPanel() {
  const [activeTab, setActiveTab] = useState<Tab>('sync');
  const { data: gcConfig } = useGoCardlessConfig();

  // Don't render if GoCardless is not configured
  if (!gcConfig?.enabled) {
    return null;
  }

  return (
    <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-teal-50/50 to-white border-b border-white/40">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Users className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Migration des mandats</h3>
            <p className="text-sm text-gray-500">
              Migrer vos mandats GoCardless existants vers la plateforme
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        <TabButton
          active={activeTab === 'sync'}
          onClick={() => setActiveTab('sync')}
          icon={<RefreshCw className="w-4 h-4" />}
          label="Synchronisation API"
        />
        <TabButton
          active={activeTab === 'import'}
          onClick={() => setActiveTab('import')}
          icon={<Upload className="w-4 h-4" />}
          label="Import CSV"
        />
        <TabButton
          active={activeTab === 'manual'}
          onClick={() => setActiveTab('manual')}
          icon={<Link2 className="w-4 h-4" />}
          label="Association manuelle"
        />
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'sync' && <SyncTab />}
        {activeTab === 'import' && <ImportTab />}
        {activeTab === 'manual' && <ManualTab />}
      </div>
    </div>
  );
}

// ==================== TAB BUTTON ====================

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`
        flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium
        transition-colors border-b-2
        ${
          active
            ? 'text-teal-600 border-teal-600 bg-teal-50/50'
            : 'text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50'
        }
      `}
    >
      {icon}
      {label}
    </button>
  );
}

// ==================== SYNC TAB ====================

function SyncTab() {
  const [autoAssociate, setAutoAssociate] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string[]>(['active']);
  const syncMutation = useSyncMandates();

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync({
        autoAssociate,
        statusFilter,
      });
    } catch (error) {
      logger.error('Sync failed:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-sm text-gray-600">
          Cette option récupère automatiquement tous vos mandats depuis votre compte GoCardless et
          tente de les associer à vos clients existants en comparant les emails et noms.
        </p>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="autoAssociate"
            checked={autoAssociate}
            onChange={(e) => setAutoAssociate(e.target.checked)}
            className="w-5 h-5 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
          />
          <label htmlFor="autoAssociate" className="text-sm text-gray-700">
            Association automatique (score de correspondance &gt;= 80%)
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrer par statut
          </label>
          <div className="flex flex-wrap gap-2">
            {['active', 'pending_customer_approval', 'pending_submission'].map((status) => (
              <button
                key={status}
                onClick={() =>
                  setStatusFilter((prev) =>
                    prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
                  )
                }
                className={`
                  px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                  ${
                    statusFilter.includes(status)
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {status.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sync Button */}
      <button
        onClick={handleSync}
        disabled={syncMutation.isPending}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
      >
        {syncMutation.isPending ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Synchronisation en cours...
          </>
        ) : (
          <>
            <RefreshCw className="w-5 h-5" />
            Synchroniser avec GoCardless
          </>
        )}
      </button>

      {/* Results */}
      {syncMutation.isSuccess && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-100">
          <h4 className="font-medium text-green-800 mb-3">Synchronisation terminee</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">
                {syncMutation.data.totalFetched}
              </p>
              <p className="text-xs text-green-600">Recuperes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {syncMutation.data.newMandates}
              </p>
              <p className="text-xs text-blue-600">Nouveaux</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-teal-600">
                {syncMutation.data.autoMatched}
              </p>
              <p className="text-xs text-teal-600">Auto-associes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">
                {syncMutation.data.unmatched}
              </p>
              <p className="text-xs text-yellow-600">A associer</p>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {syncMutation.isError && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-red-50 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{syncMutation.error.message}</p>
        </div>
      )}
    </div>
  );
}

// ==================== IMPORT TAB ====================

function ImportTab() {
  const [csvData, setCsvData] = useState<CSVImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportMandates();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      try {
        const rows = parseCSV(text);
        setCsvData(rows);
      } catch (error: any) {
        setParseError(error.message);
      }
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string): CSVImportRow[] => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('Le fichier CSV doit contenir au moins un en-tete et une ligne de donnees');
    }

    const headers = lines[0].toLowerCase().split(',').map((h) => h.trim());
    const mandateIdIndex = headers.findIndex(
      (h) => h.includes('mandate') || h.includes('gocardless')
    );
    const emailIndex = headers.findIndex((h) => h.includes('email'));
    const clientIdIndex = headers.findIndex((h) => h.includes('client') && h.includes('id'));

    if (mandateIdIndex === -1) {
      throw new Error('Colonne gocardlessMandateId non trouvee');
    }

    const rows: CSVImportRow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map((v) => v.trim().replace(/^["']|["']$/g, ''));
      if (values[mandateIdIndex]) {
        rows.push({
          gocardlessMandateId: values[mandateIdIndex],
          clientEmail: emailIndex !== -1 ? values[emailIndex] || undefined : undefined,
          clientId: clientIdIndex !== -1 ? values[clientIdIndex] || undefined : undefined,
        });
      }
    }

    if (rows.length === 0) {
      throw new Error('Aucune ligne valide trouvee dans le fichier');
    }

    return rows;
  };

  const handleImport = async () => {
    try {
      await importMutation.mutateAsync(csvData);
    } catch (error) {
      logger.error('Import failed:', error);
    }
  };

  const downloadTemplate = () => {
    const template = 'gocardlessMandateId,clientEmail,clientId\nMD000XXXXX,client@example.com,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mandate-migration-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-sm text-gray-600">
          Importez un fichier CSV contenant les IDs de mandats GoCardless et les emails ou IDs
          clients correspondants. Format: gocardlessMandateId, clientEmail ou clientId.
        </p>
      </div>

      {/* Template Download */}
      <button
        onClick={downloadTemplate}
        className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700"
      >
        <Download className="w-4 h-4" />
        Telecharger le modele CSV
      </button>

      {/* File Upload */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 hover:border-teal-400 hover:text-teal-600 transition-colors"
        >
          <FileText className="w-6 h-6" />
          <span>Cliquez pour selectionner un fichier CSV</span>
        </button>
      </div>

      {/* Parse Error */}
      {parseError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">{parseError}</p>
        </div>
      )}

      {/* Preview */}
      {csvData.length > 0 && (
        <div className="p-4 rounded-xl bg-gray-50">
          <p className="text-sm font-medium text-gray-700 mb-2">
            {csvData.length} lignes prete(s) a importer
          </p>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-1">Mandate ID</th>
                  <th className="py-1">Email</th>
                  <th className="py-1">Client ID</th>
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 5).map((row, i) => (
                  <tr key={i} className="border-t border-gray-200">
                    <td className="py-1 font-mono text-xs">{row.gocardlessMandateId}</td>
                    <td className="py-1">{row.clientEmail || '-'}</td>
                    <td className="py-1">{row.clientId || '-'}</td>
                  </tr>
                ))}
                {csvData.length > 5 && (
                  <tr className="border-t border-gray-200">
                    <td colSpan={3} className="py-1 text-gray-400 text-center">
                      ... et {csvData.length - 5} autres
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleImport}
            disabled={importMutation.isPending}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-teal-600 text-white font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {importMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Import en cours...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                Importer les mandats
              </>
            )}
          </button>
        </div>
      )}

      {/* Results */}
      {importMutation.isSuccess && (
        <div className="p-4 rounded-xl bg-green-50 border border-green-100">
          <h4 className="font-medium text-green-800 mb-3">Import termine</h4>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-600">{importMutation.data.total}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{importMutation.data.success}</p>
              <p className="text-xs text-green-600">Succes</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{importMutation.data.failed}</p>
              <p className="text-xs text-red-600">Echec</p>
            </div>
          </div>

          {importMutation.data.errors.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
              <p className="font-medium mb-2">Erreurs:</p>
              <ul className="list-disc list-inside space-y-1">
                {importMutation.data.errors.slice(0, 3).map((err, i) => (
                  <li key={i}>
                    Ligne {err.row}: {err.error}
                  </li>
                ))}
                {importMutation.data.errors.length > 3 && (
                  <li>... et {importMutation.data.errors.length - 3} autres erreurs</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== MANUAL TAB ====================

function ManualTab() {
  const { data: unassociated, isLoading, refetch } = useUnassociatedMandates();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchMandate, setSearchMandate] = useState('');
  const associateMutation = useAssociateMandate();

  const handleAssociate = async (gocardlessMandateId: string, clientId: string) => {
    try {
      await associateMutation.mutateAsync({ gocardlessMandateId, clientId });
      setExpandedId(null);
    } catch (error) {
      logger.error('Association failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
        <p className="text-sm text-gray-600">
          Associez manuellement les mandats GoCardless à vos clients. Les suggestions sont basées
          sur la correspondance d'email, nom d'entreprise et titulaire du compte bancaire.
        </p>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Search Mandate */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchMandate}
          onChange={(e) => setSearchMandate(e.target.value)}
          placeholder="Rechercher un ID de mandat..."
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500"
        />
      </div>

      {/* Unassociated Mandates List */}
      {!unassociated || unassociated.length === 0 ? (
        <div className="flex items-center gap-3 p-6 rounded-xl bg-gray-50 text-center justify-center">
          <Check className="w-6 h-6 text-green-500" />
          <p className="text-gray-600">Tous les mandats sont associes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {unassociated
            .filter(
              (m) =>
                !searchMandate ||
                m.gocardlessMandateId.toLowerCase().includes(searchMandate.toLowerCase())
            )
            .map((mandate) => (
              <MandateCard
                key={mandate.id}
                mandate={mandate}
                expanded={expandedId === mandate.id}
                onToggle={() => setExpandedId(expandedId === mandate.id ? null : mandate.id)}
                onAssociate={handleAssociate}
                isPending={associateMutation.isPending}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// ==================== MANDATE CARD ====================

function MandateCard({
  mandate,
  expanded,
  onToggle,
  onAssociate,
  isPending,
}: {
  mandate: UnassociatedMandate;
  expanded: boolean;
  onToggle: () => void;
  onAssociate: (mandateId: string, clientId: string) => void;
  isPending: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-gray-400" />
          <div className="text-left">
            <p className="font-mono text-sm text-gray-900">{mandate.gocardlessMandateId}</p>
            <p className="text-xs text-gray-500">
              {mandate.customerName || mandate.customerEmail || 'Client inconnu'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {mandate.suggestedClients.length} suggestion(s)
          </span>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Mandate Info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Email GoCardless</p>
              <p className="font-medium">{mandate.customerEmail || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Titulaire compte</p>
              <p className="font-medium">{mandate.accountHolderName || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">IBAN</p>
              <p className="font-medium font-mono">****{mandate.ibanLast4 || '????'}</p>
            </div>
            <div>
              <p className="text-gray-500">Statut</p>
              <p className="font-medium capitalize">{mandate.status.replace(/_/g, ' ')}</p>
            </div>
          </div>

          {/* Suggested Clients */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Clients suggeres</p>
            {mandate.suggestedClients.length > 0 ? (
              <div className="space-y-2">
                {mandate.suggestedClients.map((suggestion) => (
                  <div
                    key={suggestion.clientId}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-teal-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                        ${
                          suggestion.matchScore >= 90
                            ? 'bg-green-100 text-green-600'
                            : suggestion.matchScore >= 70
                              ? 'bg-yellow-100 text-yellow-600'
                              : 'bg-gray-100 text-gray-600'
                        }
                      `}
                      >
                        {suggestion.matchScore}%
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{suggestion.clientName}</p>
                        <p className="text-xs text-gray-500">{suggestion.matchReason}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => onAssociate(mandate.gocardlessMandateId, suggestion.clientId)}
                      disabled={isPending}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
                    >
                      {isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Link2 className="w-4 h-4" />
                          Associer
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 italic">
                Aucune suggestion trouvee. Verifiez que le client existe dans votre base.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MandateMigrationPanel;
